/**
 * RecommendedCeeFiches — Bloc « Fiches CEE recommandées » des hubs audience.
 *
 * Affiche les 4 fiches phares de l'audience (chargées en SSR via
 * getAudiencePageData → getRecommendedCeeFichesForAudience), avec lien vers
 * la fiche détaillée (+ simulateur de prime) et le catalogue complet.
 */

import { motion } from 'framer-motion';
import { ArrowRight, Calculator, FileText } from 'lucide-react';
import type { AudienceMeta } from '@shared/constants/audiences';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { getAudienceTheme } from '../../config/audience-theme';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

/** Forme sérialisée SSR (voir server/modules/cee/audience-fiches.ts) */
export interface RecommendedCeeFiche {
  code: string;
  label: string;
  sector: string;
  primeEstimate: string | null;
  primeMin: number | null;
  primeMax: number | null;
  calculable: boolean;
  url: string;
}

const SECTOR_BADGES: Record<string, string> = {
  BAR: 'bg-brand-100 text-brand-800',
  BAT: 'bg-lavender-100 text-lavender-800',
  IND: 'bg-accent-100 text-accent-800',
  AGRI: 'bg-sage-100 text-sage-700',
  TRA: 'bg-stone-100 text-stone-700',
  RES: 'bg-stone-100 text-stone-700',
};

export interface RecommendedCeeFichesProps {
  audience: AudienceMeta;
  fiches: RecommendedCeeFiche[];
  /** Eyebrow custom (défaut : "Primes CEE") */
  eyebrow?: string;
  /** Titre custom */
  title?: string;
}

export function RecommendedCeeFiches({
  audience,
  fiches,
  eyebrow = 'Primes CEE',
  title,
}: RecommendedCeeFichesProps) {
  if (fiches.length === 0) return null;

  const theme = getAudienceTheme(audience.id);
  const heading =
    title ?? `Les fiches CEE les plus utilisées en ${audience.label.toLowerCase()}`;

  return (
    <section className="bg-stone-50/70 py-16 md:py-20">
      <Container>
        <Reveal>
          <div className="mx-auto mb-10 max-w-2xl text-center md:mb-12">
            <p className="eyebrow justify-center">{eyebrow}</p>
            <h2 className="mt-3 h-section">{heading}</h2>
            <p className="mt-3 text-[14px] leading-relaxed text-stone-500">
              Chaque opération standardisée ouvre droit à une prime — montant
              calculable en ligne sur les fiches officielles du catalogue ATEE.
            </p>
          </div>
        </Reveal>

        <div className="mx-auto grid max-w-5xl gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          {fiches.slice(0, 4).map((fiche, idx) => (
            <motion.a
              key={fiche.code}
              href={fiche.url}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45, delay: idx * 0.05, ease: easeStripe }}
              className="group flex flex-col rounded-xl border border-stone-200/70 bg-paper p-4 transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] font-bold tracking-tight text-stone-500">
                  {fiche.code}
                </span>
                <span
                  className={cn(
                    'rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                    SECTOR_BADGES[fiche.sector] ?? 'bg-stone-100 text-stone-700',
                  )}
                >
                  {fiche.sector}
                </span>
              </div>

              <h3 className="mt-2.5 font-display text-[13.5px] font-semibold leading-snug text-ink">
                {fiche.label}
              </h3>

              <div className="mt-auto pt-3">
                {fiche.primeEstimate && (
                  <p className="text-[12px] font-semibold text-stone-700">
                    {fiche.primeEstimate}
                  </p>
                )}
                <p
                  className={cn(
                    'mt-1.5 inline-flex items-center gap-1.5 text-[11.5px] font-semibold',
                    theme.chipIcon ?? 'text-brand-700',
                  )}
                >
                  {fiche.calculable ? (
                    <>
                      <Calculator className="h-3 w-3" strokeWidth={2} />
                      Simuler ma prime
                    </>
                  ) : (
                    <>
                      <FileText className="h-3 w-3" strokeWidth={2} />
                      Voir la fiche
                    </>
                  )}
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </p>
              </div>
            </motion.a>
          ))}
        </div>

        <Reveal>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="/cee/fiches"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-stone-700 underline-offset-4 transition-colors hover:text-ink hover:underline"
            >
              Parcourir les 261 fiches du catalogue
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <span className="hidden text-stone-300 sm:inline">·</span>
            <a
              href={
                audience.id === 'particuliers'
                  ? '/particuliers/calculer-mes-aides'
                  : `${audience.href}/diagnostic-cee`
              }
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-stone-700 underline-offset-4 transition-colors hover:text-ink hover:underline"
            >
              {audience.id === 'particuliers'
                ? 'Calculer toutes mes aides'
                : 'Diagnostic CEE personnalisé'}
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
